import { Dialog } from "primereact/dialog";
import JobHistoryDialog from "./JobHistoryDialog";
import ReferenceTimePickerDialog from "./ReferenceTimePickerDialog";
import TestDataDialog from "./TestDataDialog";
import LogsDialog from "./LogsDialog";
import CreateCodeFileDialog from "./CreateCodeFileDialog";
import AddNewJobDialog from "./AddNewJobDialog";
import DeleteJobDialog from "./DeleteJobDialog";
import CodeAssign from "./CodeAssign";
import { DialogType } from "./hooks/useJobRunnerDialogs";
import { JobRunnerTabTypes } from "./JobRunner";
import {
  CodeFileInfo,
  ExecutionResultDTO,
  JobInfoDTO,
  PythonTaskData,
} from "../../../serviceapi/jobRunnerApi";

interface JobRunnerDialogsProps {
  openDialog: DialogType;
  closeDialog: () => void;

  // History
  selectedJob: JobInfoDTO | null;

  // Test data
  openTestDataDialogWithFetch: (referenceTime?: string) => void;
  currentTestData: PythonTaskData | null;

  // Logs
  currentExecutionsData: ExecutionResultDTO[] | null;
  handleFetchExecutions: () => void;

  // Create file
  newFileName: string;
  setNewFileName: (name: string) => void;
  createBlankPythonFile: () => void;

  // Assign code
  currentTab: JobRunnerTabTypes;
  codeFileId: number | undefined;
  codeFilesList: CodeFileInfo[];
  selectedCodeFile: CodeFileInfo | null;
  setSelectedCodeFile: (file: CodeFileInfo | null) => void;
  setFileToUpload: (file: File | null) => void;
  loadTaskCode: () => void;
  uploadTaskCode: () => void;
  openCreateFileDialog: () => void;
  createEmptyFileForJob: () => void;

  // Add job
  jobs: JobInfoDTO[];
  handleAddNewJob: (job: JobInfoDTO) => void;

  // Delete job
  handleRemoveJob: () => void;
}

const JobRunnerDialogs = ({
  openDialog,
  closeDialog,
  selectedJob,
  openTestDataDialogWithFetch,
  currentTestData,
  currentExecutionsData,
  handleFetchExecutions,
  newFileName,
  setNewFileName,
  createBlankPythonFile,
  currentTab,
  codeFileId,
  codeFilesList,
  selectedCodeFile,
  setSelectedCodeFile,
  setFileToUpload,
  loadTaskCode,
  uploadTaskCode,
  openCreateFileDialog,
  createEmptyFileForJob,
  jobs,
  handleAddNewJob,
  handleRemoveJob,
}: JobRunnerDialogsProps) => {
  const showAssignCode =
    openDialog === "assignCode" &&
    ((currentTab === JobRunnerTabTypes.CODE && codeFileId != null) ||
      currentTab === JobRunnerTabTypes.CONFIG);

  return (
    <>
      {openDialog === "history" && selectedJob && (
        <JobHistoryDialog
          isVisible
          setIsVisible={() => closeDialog()}
          job={selectedJob}
        />
      )}

      {openDialog === "referenceTimePicker" && (
        <ReferenceTimePickerDialog
          isVisible
          onHide={closeDialog}
          onConfirm={openTestDataDialogWithFetch}
        />
      )}

      {openDialog === "testData" && currentTestData && (
        <TestDataDialog
          isVisible
          setIsVisible={() => closeDialog()}
          testData={currentTestData}
        />
      )}

      {openDialog === "logs" && currentExecutionsData && (
        <LogsDialog
          isVisible
          setIsVisible={() => closeDialog()}
          executionsData={currentExecutionsData}
          refreshLogs={handleFetchExecutions}
        />
      )}

      {openDialog === "createFile" && (
        <CreateCodeFileDialog
          isCreateFileDialogVisible
          setIsCreateFileDialogVisible={() => closeDialog()}
          newFileName={newFileName}
          setNewFileName={setNewFileName}
          createBlankPythonFile={createBlankPythonFile}
        />
      )}

      {showAssignCode && (
        <Dialog
          visible
          style={{ width: "350px" }}
          onHide={closeDialog}
          header="Assign New Code"
        >
          <CodeAssign
            isCodeExist
            codeFilesList={codeFilesList}
            selectedCodeFile={selectedCodeFile}
            setSelectedCodeFile={setSelectedCodeFile}
            setFileToUpload={setFileToUpload}
            loadTaskCode={loadTaskCode}
            selectedJob={selectedJob}
            uploadTaskCode={uploadTaskCode}
            setIsCreateFileDialogVisible={openCreateFileDialog}
            createEmptyFileForJob={createEmptyFileForJob}
          />
        </Dialog>
      )}

      {openDialog === "addJob" && (
        <AddNewJobDialog
          isVisible
          setIsVisible={() => closeDialog()}
          onJobCreated={handleAddNewJob}
          existingJobs={jobs}
        />
      )}

      {openDialog === "deleteJob" && selectedJob && (
        <DeleteJobDialog
          isVisible
          setIsVisible={() => closeDialog()}
          jobTitle={selectedJob.taskTitle ?? ""}
          handleRemoveJob={handleRemoveJob}
        />
      )}
    </>
  );
};

export default JobRunnerDialogs;
